import React, { useRef, useState } from "react";
import { Upload, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const ImportCSVPanel = ({ onImported }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/import-csv", form);
      setResult(res.data);
      toast.success(`Imported: ${res.data.created} new, ${res.data.updated} updated`);
      onImported?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Import failed";
      toast.error(detail);
      setResult({ error: detail });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10" data-testid="import-csv-panel">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="overline">Bulk import attendees</div>
          <h2 className="font-serif-display text-3xl mt-1">Upload CSV</h2>
          <p className="text-sm text-slate-600 mt-2 max-w-lg">
            Columns required: <code className="text-xs bg-slate-100 px-1.5 py-0.5">first_name, email, age, occupation, gender</code>.
            Existing emails are updated; new ones are created. Imported attendees can log in with their email immediately.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            onChange={(e) => onFile(e.target.files?.[0])}
            className="hidden"
            data-testid="import-csv-file"
          />
          <button
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="mm-btn-primary"
            style={{ width: "auto", padding: "12px 20px", fontSize: 13 }}
            data-testid="import-csv-button"
          >
            <Upload size={14} strokeWidth={1.5} className="inline mr-2" />
            {busy ? "Importing…" : "Choose CSV file"}
          </button>
        </div>
      </div>

      {result && !result.error && (
        <div className="mt-4 p-4 border border-slate-200 rounded-sm bg-slate-50" data-testid="import-result">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 size={18} strokeWidth={1.5} style={{ color: "#15803D" }} />
            <span className="text-sm font-medium">
              {result.created} created · {result.updated} updated · {result.error_count} skipped
            </span>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3">
              <div className="overline text-[10px] mb-2 flex items-center gap-2">
                <AlertTriangle size={11} strokeWidth={1.5} style={{ color: "#B91C1C" }} />
                Skipped rows
              </div>
              <ul className="text-xs text-slate-600 space-y-1 max-h-40 overflow-y-auto">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.email || "noemail"}`}>
                    Row {e.row}{e.email ? ` (${e.email})` : ""}: {e.error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 text-xs text-slate-500 flex items-center gap-2">
        <FileText size={12} strokeWidth={1.5} />
        <span>Example: <code className="bg-slate-100 px-1 py-0.5">first_name,email,age,occupation,gender</code> then one row per attendee.</span>
      </div>
    </div>
  );
};

export default ImportCSVPanel;
